import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getCurrentRealm } from "../keycloak";
import { isSSBAMLEnabled } from "../featureFlags";

//const API_BASE = "http://localhost:9191";
const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
    ? "http://localhost:9191"
    : "https://lionfish-app-2-aehjc.ondigitalocean.app");

  const AML_API =
    process.env.REACT_APP_SSB_AML_API_BASE ||
    (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
      ? "http://localhost:9191/v1/ssb/aml"
      : "https://lionfish-app-2-aehjc.ondigitalocean.app/v1/ssb/aml");
const money0 = (n) =>
  n == null || Number.isNaN(Number(n))
    ? "—"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      }).format(Number(n));

const pct1 = (n) =>
  n == null || Number.isNaN(Number(n)) ? "—" : `${Number(n).toFixed(1)}%`;

const short = (s) => (s || "").slice(0, 10);

const Pill = ({ text, tone = "info" }) => {
  const map = {
    info: { bg: "#EFF8FF", fg: "#175CD3" },
    neutral: { bg: "#EEF2FF", fg: "#3730A3" },
    good: { bg: "#ECFDF3", fg: "#027A48" },
    warn: { bg: "#FFFAEB", fg: "#B54708" },
    bad: { bg: "#FEF3F2", fg: "#B42318" },
  };
  const c = map[tone] || map.info;
  return (
    <span
      style={{
        padding: "4px 10px",
        borderRadius: 999,
        background: c.bg,
        color: c.fg,
        fontSize: 12,
        fontWeight: 900,
        border: "1px solid rgba(0,0,0,0.04)",
        letterSpacing: 0.2,
        whiteSpace: "nowrap",
      }}
    >
      {text}
    </span>
  );
};

const StatCard = ({ label, value, sub }) => (
  <div
    style={{
      background: "#fff",
      border: "1px solid #E7ECF3",
      borderRadius: 16,
      padding: 14,
      boxShadow: "0 10px 30px rgba(16,24,40,0.06)",
      minHeight: 92,
    }}
  >
    <div style={{ fontSize: 12, color: "#5E6B7A", fontWeight: 800, marginBottom: 8 }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 950, color: "#0B1F33", lineHeight: 1.1 }}>{value}</div>
    {sub ? <div style={{ marginTop: 6, fontSize: 12, color: "#7A8696" }}>{sub}</div> : null}
  </div>
);

const Tile = ({
  title,
  desc,
  path,
  badge,
  icon,
  question,
  defaultParams, // { week_start, month_start }
}) => {
  const navigate = useNavigate();
  const realm = getCurrentRealm();

  const open = () => {
    const qs = new URLSearchParams();
    qs.set("realm", realm);
    if (defaultParams?.week_start) qs.set("week_start", defaultParams.week_start);
    if (defaultParams?.month_start) qs.set("month_start", defaultParams.month_start);
    navigate(`${path}?${qs.toString()}`);
  };

  return (
    <div
      onClick={open}
      style={{
        cursor: "pointer",
        background: "#fff",
        border: "1px solid #E7ECF3",
        borderRadius: 18,
        padding: 16,
        boxShadow: "0 14px 40px rgba(16,24,40,0.08)",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 20px 55px rgba(16,24,40,0.12)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0px)";
        e.currentTarget.style.boxShadow = "0 14px 40px rgba(16,24,40,0.08)";
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 12,
              background: "#F1F5FF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 18,
              fontWeight: 900,
              color: "#1D4ED8",
              border: "1px solid #E7ECF3",
            }}
          >
            {icon}
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 950, color: "#0B1F33" }}>{title}</div>
            {question ? (
              <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>
                <b>Answers:</b> {question}
              </div>
            ) : null}
          </div>
        </div>

        {badge ? <Pill text={badge.text} tone={badge.tone} /> : null}
      </div>

      <div style={{ marginTop: 10, color: "#64748B", fontSize: 13, lineHeight: 1.35 }}>{desc}</div>

      <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 12, color: "#1D4ED8", fontWeight: 900 }}>
          Open report →
        </div>
        <div style={{ fontSize: 12, color: "#94A3B8" }}>
          {defaultParams?.week_start ? `Week: ${short(defaultParams.week_start)}` : ""}
          {defaultParams?.month_start ? `Month: ${short(defaultParams.month_start)}` : ""}
        </div>
      </div>
    </div>
  );
};

export default function ReportingDashboardHome() {
  const [programs, setPrograms] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [months, setMonths] = useState([]);

  const [portfolioMonthTotal, setPortfolioMonthTotal] = useState(null);
  const [top3Pct, setTop3Pct] = useState(null);
  const [loading, setLoading] = useState(false);

  const latestWeek = weeks[0] || "";
  const latestMonth = months[0] || "";

  // Fetch filters + lightweight executive metrics
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        // 1) programs + weeks
        const f1 = await fetch(`${API_BASE}/v1/ssb/filters/counterparty-concentration`).then((r) => r.json());
        // 2) months for program concentration
        const f2 = await fetch(`${API_BASE}/v1/ssb/filters/program-concentration`).then((r) => r.json());

        if (!mounted) return;

        const p = Array.isArray(f1.programs) ? f1.programs : [];
        const w = Array.isArray(f1.weeks) ? f1.weeks : [];
        const m = Array.isArray(f2.months) ? f2.months : [];

        setPrograms(p);
        setWeeks(w);
        setMonths(m);

        // optional: fetch month concentration to compute top3 %
        if (m[0]) {
          const url = new URL(`${API_BASE}/v1/ssb/programs/concentration`);
          url.searchParams.set("month_start", m[0]);
          const pc = await fetch(url.toString()).then((r) => r.json());
          const rows = Array.isArray(pc.rows) ? pc.rows : [];
          const total = rows.reduce((s, r) => s + Number(r.total_amount || 0), 0);
          const top3 = rows.slice(0, 3).reduce((s, r) => s + Number(r.total_amount || 0), 0);
          const top3Share = total > 0 ? (top3 / total) * 100 : 0;

          setPortfolioMonthTotal(total);
          setTop3Pct(top3Share);
        }
      } catch (e) {
        console.error("ReportingDashboardHome load failed:", e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const concentrationTone = useMemo(() => {
    if (top3Pct == null) return "neutral";
    if (top3Pct >= 80) return "bad";
    if (top3Pct >= 65) return "warn";
    return "good";
  }, [top3Pct]);

  return (
    <div style={{ padding: 18 }}>
      {/* Hero / Executive header */}
      <div
        style={{
          background: "linear-gradient(90deg, #0B3A6E 0%, #164E86 60%, #1F63A8 100%)",
          color: "white",
          borderRadius: 18,
          padding: "16px 18px",
          boxShadow: "0 18px 60px rgba(11,58,110,0.22)",
          marginBottom: 12,
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <div>
          <div style={{ fontSize: 18, fontWeight: 950, letterSpacing: 0.2 }}>
            Reporting Dashboard — Sponsor Bank Oversight
          </div>
          <div style={{ fontSize: 12, opacity: 0.92, marginTop: 4 }}>
            Executive-ready monitoring across programs, counterparties, ticket sizes, and jurisdiction exposure.
          </div>
          <div style={{ fontSize: 11, opacity: 0.85, marginTop: 6 }}>
            {loading ? "Loading portfolio snapshot…" : `Latest week: ${short(latestWeek)} • Latest month: ${short(latestMonth)}`}
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
          <Pill text="Phase 1" tone="info" />
          <Pill text="SSB Reports" tone="neutral" />
          <Pill text="Live Data" tone="good" />
        </div>
      </div>

      {/* Executive insight banner */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #E7ECF3",
          borderRadius: 18,
          padding: 14,
          boxShadow: "0 10px 30px rgba(16,24,40,0.06)",
          marginBottom: 12,
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <Pill text="Executive Insight" tone={concentrationTone} />
            <div style={{ fontSize: 18, fontWeight: 950, color: "#0B1F33" }}>
              {top3Pct == null
                ? "Portfolio concentration snapshot will appear here."
                : `Top 3 programs represent ${top3Pct.toFixed(1)}% of monthly portfolio volume`}
            </div>
          </div>
          <div style={{ marginTop: 6, fontSize: 12, color: "#6B7280" }}>
            Total portfolio volume (latest month): <b>{portfolioMonthTotal == null ? "—" : money0(portfolioMonthTotal)}</b>
            {" • "}
            Programs observed: <b>{programs.length || "—"}</b>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Pill text={loading ? "Loading…" : "Ready"} tone={loading ? "neutral" : "good"} />
        </div>
      </div>

      {/* KPI row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 12 }}>
        <StatCard label="Portfolio Volume (latest month)" value={portfolioMonthTotal == null ? "—" : money0(portfolioMonthTotal)} sub={short(latestMonth)} />
        <StatCard label="Programs Active" value={programs.length ? String(programs.length) : "—"} sub="Distinct program_name" />
        <StatCard label="Latest Week" value={latestWeek ? short(latestWeek) : "—"} sub="For weekly views" />
        <StatCard label="Top 3 Concentration" value={top3Pct == null ? "—" : pct1(top3Pct)} sub="Portfolio concentration risk" />
      </div>

      {/* Report tiles */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12 }}>
        <Tile
          icon="CP"
          title="Counterparty Concentration"
          question="Who are the largest counterparties and how concentrated is exposure?"
          desc="Top counterparties by volume or count, plus concentration trend (Top 1 / Top 5)."
          path="/reporting/counterparty-concentration"
          badge={{ text: "LIVE", tone: "good" }}
          defaultParams={{ week_start: latestWeek }}
        />

        <Tile
          icon="PG"
          title="Program Concentration"
          question="Which fintech programs dominate portfolio volume?"
          desc="Portfolio share by program plus Top 3 / Top 5 concentration signal."
          path="/reporting/program-concentration"
          badge={{ text: "LIVE", tone: "good" }}
          defaultParams={{ month_start: latestMonth }}
        />

        <Tile
          icon="TX"
          title="Ticket Size Distribution"
          question="Is the portfolio dominated by high-value tickets?"
          desc="Band distribution (<1k, 1k–10k, 10k–50k, 50k+) with high-value dominance signal and trend."
          path="/reporting/ticket-bands"
          badge={{ text: "LIVE", tone: "good" }}
          defaultParams={{ month_start: latestMonth }}
        />

        <Tile
          icon="GEO"
          title="Country Exposure"
          question="Where is money going and how cross-border is the program?"
          desc="Top countries by volume/count with non-US exposure signal and trend."
          path="/reporting/country-exposure"
          badge={{ text: "LIVE", tone: "good" }}
          defaultParams={{ month_start: latestMonth }}
        />
                    <Tile
            icon="HM"
            title="Portfolio Risk Heatmap"
            question="Which programs need enhanced monitoring right now?"
            desc="Composite posture using concentration, cross-border exposure, and high-value dominance."
            path="/reporting/portfolio-risk-heatmap"
            badge={{ text: "NEW", tone: "warn" }}
            defaultParams={{ week_start: latestWeek, month_start: latestMonth }}
            />
         {isSSBAMLEnabled && (
          <Tile
            icon="AML"
            title="SSB Risk Console"
            question="Which accounts were flagged for review or block by the AML rules engine?"
            desc="Date-wise pass / review / block decisions across all SSB fintech programs, with per-account drill-down and fired rules."
            path="/reporting/aml-execution"
            badge={{ text: "LIVE", tone: "good" }}
          />
        )}
        {isSSBAMLEnabled && (
          <Tile
            icon="HRJ"
            title="High-Risk Jurisdiction Alerts"
            question="Which programs are trending toward or exceeding High-Risk Jurisdiction thresholds, and where is that exposure going?"
            desc="Date-range hit trend per program vs. threshold, plus per-program country breakdown — the same data behind the Slack/email alert."
            path="/reporting/high-risk-alerts"
            badge={{ text: "NEW", tone: "warn" }}
          />
        )}
      </div>
      

      <div style={{ marginTop: 12, fontSize: 12, color: "#64748B" }}>
        Tip: start with <b>Counterparty Concentration</b> for sponsor-bank discussions; it maps directly to concentration oversight expectations.
      </div>
    </div>
  );
}